import { computed, ref, watch, type Ref } from 'vue';
import type { ResolvedRoute } from '@router/interfaces/resolved-route';

export interface MenuDefinition {
    identifier: string,
    title: string|null,
    route: string|null,
    additionalRoutes: Array<string>,
    children: Array<MenuDefinition>,
}

export interface NavigationEntry {
    id: string,
    parent: string|null,
    parents: Array<string>,
    title: string,
    route: ResolvedRoute|null,
    role: string|null,
    keywords: Array<string>,
    children: Array<NavigationEntry>,
    level: number,
}

/**
 * @param menu Vollständiger Menübaum
 * @param resolveRoute Callback um Routennamen in Routendaten aufzulösen
 */
export function prepareNavigation(menu: MenuDefinition, resolveRoute: (name: string) => ResolvedRoute) {
    const entries: Array<NavigationEntry> = [];
    const entriesByRoute = new Map<string, Array<NavigationEntry>>();

    /**
     * @param definition Eintrag aus der statischen Menüdefinition
     * @param parents IDs der sichtbaren Eltern
     * @param keywords Suchbegriffe der Eltern
     */
    function prepare(definition: MenuDefinition, parents: Array<string>, keywords: Array<string>): NavigationEntry {
        const route = definition.route ? resolveRoute(definition.route) : null;
        const entry: NavigationEntry = {
            id: definition.identifier,
            parent: parents[parents.length - 1] ?? null,
            parents: parents,
            title: definition.title ?? route?.meta.title ?? '',
            route,
            role: route?.meta.role ?? null,
            keywords: [...keywords, definition.title, route?.meta.title].filter((word): word is string => Boolean(word)).map((word) => word.toLowerCase()),
            children: [],
            level: parents.length + 1,
        };
        entries.push(entry);
        for (const name of [definition.route, ...definition.additionalRoutes]) {
            if (name) {
                entriesByRoute.set(name, [...entriesByRoute.get(name) ?? [], entry]);
            }
        }
        entry.children = definition.children.map((child) => prepare(child, [...parents, entry.id], entry.keywords));
        return entry;
    }

    const roots = menu.children.map((child) => prepare(child, [], []));
    return { roots, entries, byRoute: entriesByRoute };
}

/**
 * @param prepared aufbereitetes, aussortiertes Menu
 * @param roles Rollen des aktuellen Benutzers
 * @param routeName name der aktuellen Route
 * @param search Suchbegriff
 */
export function useNavigation(
    prepared: ReturnType<typeof prepareNavigation>,
    roles: Ref<Array<string>>,
    routeName: Ref<string>,
    search: Ref<string|null>,
) {
    const opened = ref(new Set<string>());
    const searchTerm = computed(() => {
        const term = search.value?.trim().toLowerCase() ?? '';
        return term.length >= 3 ? term : '';
    });

    const permissions = computed(() => {
        const roleSet = new Set(roles.value);
        const allowed = new Set<string>();
        const accessible = new Set<string>();

        function visit(entry: NavigationEntry) {
            entry.children.forEach(visit);
            if (entry.route && (!entry.role || roleSet.has(entry.role))) {
                allowed.add(entry.id);
            }
            if (allowed.has(entry.id) || entry.children.some((child) => accessible.has(child.id))) {
                accessible.add(entry.id);
            }
        }
        prepared.roots.forEach(visit);
        return { allowed, accessible };
    });

    const active = computed(() => {
        const ids = new Set<string>();
        for (const entry of prepared.byRoute.get(routeName.value) ?? []) {
            if (permissions.value.allowed.has(entry.id)) {
                [...entry.parents, entry.id].forEach((id) => ids.add(id));
            }
        }
        return ids;
    });

    watch(active, () => {
        opened.value = new Set(active.value);
    }, { immediate: true });

    watch(searchTerm, (term, previous) => {
        if (!term && previous) {
            opened.value = new Set(active.value);
        }
    });

    const visible = computed(() => {
        const { accessible, allowed } = permissions.value;
        const matches = new Set<string>();
        if (searchTerm.value) {
            for (const entry of prepared.entries) {
                if (allowed.has(entry.id) && entry.keywords.some((keyword) => keyword.includes(searchTerm.value))) {
                    [...entry.parents, entry.id].forEach((id) => matches.add(id));
                }
            }
        }
        const rows: Array<{ entry: NavigationEntry, route: ResolvedRoute|null, active: boolean, selected: boolean, hasChildren: boolean }> = [];

        /**
         * @param entry Eintrag, der bei passender Rolle und Suche zur sichtbaren Liste hinzugefügt wird
         */
        function visit(entry: NavigationEntry) {
            if (!accessible.has(entry.id) || searchTerm.value && !matches.has(entry.id)) {
                return;
            }
            const hasChildren = entry.children.some((child) => accessible.has(child.id) && (!searchTerm.value || matches.has(child.id)));
            const selected = Boolean(searchTerm.value) || opened.value.has(entry.id) || active.value.has(entry.id);
            rows.push({ entry, route: allowed.has(entry.id) ? entry.route : null, active: active.value.has(entry.id), selected, hasChildren });
            if (selected) {
                entry.children.forEach(visit);
            }
        }
        prepared.roots.forEach(visit);
        return rows;
    });

    /**
     * @param entry Angeklickter Menüeintrag
     */
    function toggle(entry: NavigationEntry) {
        if (searchTerm.value || active.value.has(entry.id)) {
            return;
        }
        const next = new Set(opened.value);
        if (next.has(entry.id)) {
            for (const candidate of prepared.entries) {
                if (candidate.id === entry.id || candidate.parents.includes(entry.id)) {
                    next.delete(candidate.id);
                }
            }
        } else {
            const siblings = prepared.entries.filter((candidate) => candidate.parent === entry.parent && candidate.id !== entry.id);
            for (const candidate of prepared.entries) {
                if (siblings.some((sibling) => candidate.id === sibling.id || candidate.parents.includes(sibling.id))) {
                    next.delete(candidate.id);
                }
            }
            next.add(entry.id);
        }
        opened.value = next;
    }

    return { visible, toggle, searchTerm, permissions };
}
